// BraceViewport3D.tsx — Lightweight 3D canvas for the Brace Plan view.
// Renders only the active floor's panels + brace geometry + slab.
// Reads shadingMode / showGrid / showGround / showPanelMarks / viewPreset from store
// so all ViewerToolbar toggles work identically to other 3D views.

import { useRef, useMemo, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { Grid, Environment } from '@react-three/drei';
import * as THREE from 'three';
import GridLabels, { snapGridExtent } from './viewer/GridLabels';
import { useShallowStore } from '../store/useStore';
import type { PanelData, BracePlanSettings } from '../types/panelvar';
import type { WallSegment } from './SiteViewport3D';
import { computeBraces } from '../utils/computeBraces';
import PanelMesh3D from './viewer/PanelMesh3D';
import BraceLayer from './viewer/BraceLayer';
import OrbitCameraRig from './viewer/OrbitCameraRig';
import ViewerToolbar from './viewer/ViewerToolbar';
import NorthArrow from './viewer/NorthArrow';

const ALL_LAYERS = { void: true, recess: true, proud: true, embed: true };

interface Props {
  segments: WallSegment[];
  allPanels: PanelData[];
  checkedPanelIds: Set<string>;
  globalSettings: BracePlanSettings;
  panelOverrides: Record<string, Partial<BracePlanSettings>>;
  jointWidth?: number;
  /** Building footprint corners for slab rendering (XZ plane) */
  slabVertices?: [number, number][];
  /** Y base of this floor (inches) */
  baseElevation?: number;
}

function segmentLayout(panels: PanelData[], jointWidth: number, leftEnd: number, rightEnd: number) {
  const offsets: number[] = [];
  let x = leftEnd;
  for (let i = 0; i < panels.length; i++) {
    offsets.push(x);
    const gap = i < panels.length - 1 ? jointWidth : rightEnd;
    x += panels[i].mpvar.width + gap;
  }
  return offsets;
}

export default function BraceViewport3D({
  segments,
  allPanels,
  checkedPanelIds,
  globalSettings,
  panelOverrides,
  jointWidth = 0.75,
  slabVertices,
  baseElevation = 0,
}: Props) {
  const { ui, clearViewPreset } = useShallowStore(s => ({
    ui: s.ui,
    clearViewPreset: s.clearViewPreset,
  }));

  const zoomRef    = useRef<((factor: number) => void) | null>(null);
  const panRef     = useRef<((dx: number, dy: number) => void) | null>(null);
  const fitViewRef = useRef<(() => void) | null>(null);
  const viewerRef  = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = viewerRef.current;
    if (!el) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'h' || e.key === 'H' || e.key === 'f' || e.key === 'F') {
        clearViewPreset(); fitViewRef.current?.();
      } else if (e.key === '+' || e.key === '=') { zoomRef.current?.(0.8); clearViewPreset(); }
      else if (e.key === '-') { zoomRef.current?.(1.25); clearViewPreset(); }
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [clearViewPreset]);

  // Compute braces (pure)
  const braces = useMemo(
    () => computeBraces(segments, allPanels, checkedPanelIds, globalSettings, panelOverrides, jointWidth),
    [segments, allPanels, checkedPanelIds, globalSettings, panelOverrides, jointWidth],
  );

  // Per-segment layout offsets + bounding box
  const { layouts, boundingCenter, boundingSize, panelsKey, bounds } = useMemo(() => {
    const lays = segments.map(seg => segmentLayout(seg.panels, jointWidth, seg.leftEndJoint, seg.rightEndJoint));
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity, maxH = 0;
    segments.forEach((seg, si) => {
      if (seg.panels.length === 0) return;
      const cos = Math.cos(seg.rotationY);
      const sin = -Math.sin(seg.rotationY);
      const offsets = lays[si];
      const lastOffset = offsets.length > 0 ? offsets[offsets.length - 1] : 0;
      const totalW = lastOffset + (seg.panels[offsets.length - 1]?.mpvar.width ?? 0);
      minX = Math.min(minX, seg.x, seg.x + cos * totalW);
      maxX = Math.max(maxX, seg.x, seg.x + cos * totalW);
      minZ = Math.min(minZ, seg.z, seg.z + sin * totalW);
      maxZ = Math.max(maxZ, seg.z, seg.z + sin * totalW);
      maxH = Math.max(maxH, baseElevation + Math.max(0, ...seg.panels.map(p => p.mpvar.height)));
    });
    if (!Number.isFinite(minX)) { minX = 0; maxX = 120; minZ = 0; maxZ = 0; maxH = baseElevation + 168; }
    const cx = (minX + maxX) / 2, cz = (minZ + maxZ) / 2;
    return {
      layouts: lays,
      boundingCenter: { x: cx, y: (baseElevation + (maxH || baseElevation + 168)) / 2, z: cz },
      boundingSize: { sx: (maxX - minX) || 120, sy: (maxH - baseElevation) || 168, sz: (maxZ - minZ) || 1 },
      panelsKey: segments.map(s => s.elevId).join(',') + '|' + baseElevation,
      bounds: { minX, maxX, minZ, maxZ },
    };
  }, [segments, jointWidth, baseElevation]);

  const sizeRef   = useRef(boundingSize);
  const centerRef = useRef(boundingCenter);
  sizeRef.current   = boundingSize;
  centerRef.current = boundingCenter;

  // Slab geometry from floor footprint vertices
  const slabGeo = useMemo(() => {
    if (!slabVertices || slabVertices.length < 3) return null;
    const shape = new THREE.Shape();
    shape.moveTo(slabVertices[0][0], -slabVertices[0][1]);
    for (let i = 1; i < slabVertices.length; i++) {
      shape.lineTo(slabVertices[i][0], -slabVertices[i][1]);
    }
    shape.closePath();
    return new THREE.ShapeGeometry(shape);
  }, [slabVertices]);

  const checkedSet = useMemo(
    () => new Set(segments.flatMap(s => s.panels.map(p => p.id)).filter(id => checkedPanelIds.has(id))),
    [segments, checkedPanelIds],
  );

  return (
    <div className="viewer-root" tabIndex={-1} ref={viewerRef}>
      <ViewerToolbar
        onCenterView={() => { clearViewPreset(); fitViewRef.current?.(); }}
        onZoomIn={() => { zoomRef.current?.(0.8); clearViewPreset(); }}
        onZoomOut={() => { zoomRef.current?.(1.25); clearViewPreset(); }}
        onPanLeft={() => { panRef.current?.(30, 0); clearViewPreset(); }}
        onPanRight={() => { panRef.current?.(-30, 0); clearViewPreset(); }}
        onPanUp={() => { panRef.current?.(0, -30); clearViewPreset(); }}
        onPanDown={() => { panRef.current?.(0, 30); clearViewPreset(); }}
      />
      <Canvas
        frameloop="demand"
        camera={{ fov: 45, near: 0.1, far: 1_000_000, position: [0, 0, 500] }}
        gl={{ antialias: true }}
        shadows
        style={{ background: '#0d1117' }}
      >
        <OrbitCameraRig
          preset={ui.viewPreset}
          contentKey={panelsKey}
          sizeRef={sizeRef}
          centerRef={centerRef}
          zoomRef={zoomRef}
          panRef={panRef}
          fitViewRef={fitViewRef}
          onManualMove={clearViewPreset}
        />

        <ambientLight intensity={0.6} />
        <directionalLight position={[3000, 5000, 2000]} intensity={1.2} castShadow />
        <directionalLight position={[-2000, 1000, -1000]} intensity={0.4} />

        {ui.shadingMode === 'shaded' && (
          <Environment preset="warehouse" background={false} />
        )}

        {/* Panels */}
        {segments.map((seg, si) => {
          const offsets = layouts[si];
          return (
            <group
              key={`${seg.elevId}-${seg.floorId ?? ''}`}
              position={[seg.x, seg.baseElevation ?? 0, seg.z]}
              rotation={[0, seg.rotationY, 0]}
            >
              {seg.panels.map((p, pi) => (
                <group key={p.id} position={[offsets[pi], (p.mpvar.botElev ?? 0) * 12, 0]}>
                  <PanelMesh3D
                    panel={p}
                    shading={ui.shadingMode}
                    showPanelMarks={ui.showPanelMarks}
                    isSelected={checkedSet.has(p.id)}
                    layerVisibility={ALL_LAYERS}
                  />
                </group>
              ))}
            </group>
          );
        })}

        {/* Braces */}
        <BraceLayer braces={braces} />

        {/* Concrete slab — shown when Ground is toggled or always for brace context */}
        {slabGeo && (
          <mesh
            geometry={slabGeo}
            rotation={[-Math.PI / 2, 0, 0]}
            position={[0, baseElevation, 0]}
            receiveShadow
          >
            <meshStandardMaterial
              color="#888880"
              roughness={0.95}
              metalness={0.0}
              side={THREE.DoubleSide}
            />
          </mesh>
        )}

        {/* Ground slab (showGround toggle) — solid plane behind the footprint */}
        {ui.showGround && (
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[boundingCenter.x, baseElevation - 1, boundingCenter.z]} receiveShadow>
            <planeGeometry args={[200_000, 200_000]} />
            <meshStandardMaterial color="#2a2a2a" roughness={0.95} />
          </mesh>
        )}

        {(() => {
          const ge = snapGridExtent(bounds.minX, bounds.maxX, bounds.minZ, bounds.maxZ, 600);
          return (
            <>
              <NorthArrow origin={[ge.xStart, ge.zStart]} />

              <Grid
                args={[ge.width, ge.depth]}
                cellSize={120}
                cellThickness={0}
                cellColor="#3a5a80"
                sectionSize={600}
                sectionThickness={2.5}
                sectionColor="#5080b0"
                fadeDistance={50_000}
                visible={ui.showGrid}
                position={[ge.cx, baseElevation, ge.cz]}
              />

              {ui.showGrid && <GridLabels extent={ge} />}
            </>
          );
        })()}
      </Canvas>

      {checkedPanelIds.size === 0 && (
        <div className="viewer-no-panel">
          No panels selected — check panels in the left sidebar.
        </div>
      )}
    </div>
  );
}
